import React from 'react';
import { createPortal } from 'react-dom';
import WebViewer from '@pdftron/webviewer';
import { OverlayDrawer } from '@fluentui/react-components';
import { Dismiss20Regular, ArrowDownload16Regular } from '@fluentui/react-icons';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import viewerStyles from '../Dataroom2.0/css/index.module.css';
import vdrStyles from './css/vdr.module.css';
import { GetAuthenticatedDocUrl, GetIRLDocData } from '../../redux/documentsSlice';
import {
    downloadVdrDocumentFile,
    createVdrDocumentObjectUrl,
    loadVdrDocumentTextContent,
    resolveVdrDocumentWorkstreamId,
} from '../../utils/vdrDocumentDownloadUtils';
import {
    EXCEL_TOO_LARGE_PREVIEW_MESSAGE,
    getFileExtensionFromFile,
    getNativeTextPreviewMode,
    getWebViewerIframeOptions,
    isNativeTextPreviewFile,
    isPDFTronSupportedFile,
} from '../../utils/filePreviewUtils';
import { setupWebViewerPreviewInstance } from '../../utils/webViewerFitWidth';
import {
    classifyDocumentLoadError,
    fetchDocumentProtectionCheck,
    getProtectedDocumentMessage,
} from '../../utils/protectedDocumentUtils';
import { getYouTubeId, isAudioFile } from '../../utils/commonUtils';
import { safeOpenUrl } from '../../utils/urlValidation';

const isLinkOnlyFile = file => {
    const link = file?.document_link || file?.link_url;
    if (!link || typeof link !== 'string' || !link.trim()) return false;
    if (file?.document) return false;
    const isYouTube = link.includes('youtube') || link.includes('youtu.be');
    return !isYouTube;
};

const isYoutubeLink = file => {
    const link = file?.document_link || '';
    return link.includes('youtube') || link.includes('youtu.be');
};

const isVideoFile = file => {
    if (isYoutubeLink(file)) return true;
    const ext = getFileExtensionFromFile(file);
    return ['m4v', 'avi', 'mpg', 'mov', 'mp4', 'mp5'].includes(String(ext || '').toLowerCase());
};

const VdrFileViewerModal = ({
    openModal,
    setOpenModal,
    selectedFile,
    setSelectedFile,
    workstream,
    folderFiles = [],
    selectedFileIndex = 0,
    setSelectedFileIndex,
    isInitialProjectFile = false,
}) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    const { projectUsersAll } = useSelector(state => state?.team);

    const viewerRef = React.useRef(null);
    const instanceRef = React.useRef(null);
    const blobUrlRef = React.useRef(null);

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [fileUrl, setFileUrl] = React.useState(null);
    const [nativeText, setNativeText] = React.useState('');
    const [nativeTextMode, setNativeTextMode] = React.useState('plain');
    const [viewKind, setViewKind] = React.useState(null);

    const isProjectAdmin =
        selectedProject?.vdr?.vdr_admin === user?.id ||
        selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);

    const markRead = React.useCallback(
        file => {
            if (!file?.id || !workstream?.id) return;
            const memberId = projectUsersAll?.find(item => item?.user_id === user?.id)?.value?.group_member_id;
            const alreadyRead = file?.read_by?.includes(memberId);
            if (!alreadyRead) {
                dispatch(GetIRLDocData({ id: file.id, workstream: workstream.id }));
            }
        },
        [dispatch, projectUsersAll, user?.id, workstream?.id],
    );

    const resetViewer = React.useCallback(() => {
        instanceRef.current = null;
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
        setLoading(false);
        setError(null);
        setFileUrl(null);
        setNativeText('');
        setNativeTextMode('plain');
        setViewKind(null);
    }, []);

    const handleClose = () => {
        resetViewer();
        setOpenModal(false);
        setSelectedFile?.(null);
    };

    const handleDownload = async () => {
        const canDownload =
            isProjectAdmin ||
            selectedFile?.can_download_original ||
            selectedFile?.source === 'project_mail_attachment';
        if (!canDownload) {
            toast.info('You do not have permission to download this file');
            return;
        }
        const ok = await downloadVdrDocumentFile(dispatch, selectedFile, {
            workstream,
            isInitialProjectFile,
        });
        if (!ok) toast.error('Download failed');
    };

    const loadTextContent = async file => {
        const streamWorkstream = { id: resolveVdrDocumentWorkstreamId(file, workstream) };
        try {
            return await loadVdrDocumentTextContent(file, {
                workstream: streamWorkstream,
                isInitialProjectFile,
            });
        } catch (apiError) {
            const documentUrl = file?.document;
            if (!documentUrl) throw apiError;
            let fetchUrl = documentUrl;
            if (documentUrl.includes('.blob.core.windows.net')) {
                fetchUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
            }
            const response = await fetch(fetchUrl);
            if (!response.ok) throw apiError;
            return response.text();
        }
    };

    const loadVersionPreviewText = async (file, cancelled) => {
        const url = file?.document;
        if (!url) throw new Error('missing_url');
        const response = await fetch(url);
        if (!response.ok) throw new Error('fetch_failed');
        const text = await response.text();
        if (cancelled) return;
        setNativeText(text);
        setNativeTextMode(getNativeTextPreviewMode(getFileExtensionFromFile(file)));
        setViewKind('text');
        setLoading(false);
    };

    React.useEffect(() => {
        if (!openModal || !selectedFile) return undefined;

        let cancelled = false;
        resetViewer();
        setLoading(true);

        const run = async () => {
            try {
                if (isLinkOnlyFile(selectedFile)) {
                    setViewKind('link');
                    setLoading(false);
                    return;
                }

                if (isVideoFile(selectedFile)) {
                    const url = isYoutubeLink(selectedFile)
                        ? selectedFile.document_link
                        : selectedFile.document || selectedFile.document_link;
                    setFileUrl(url);
                    setViewKind('video');
                    markRead(selectedFile);
                    setLoading(false);
                    return;
                }

                if (isNativeTextPreviewFile(selectedFile)) {
                    if (selectedFile.versionPreview) {
                        await loadVersionPreviewText(selectedFile, cancelled);
                        return;
                    }
                    const text = await loadTextContent(selectedFile);
                    if (cancelled) return;
                    setNativeText(text);
                    setNativeTextMode(getNativeTextPreviewMode(getFileExtensionFromFile(selectedFile)));
                    setViewKind('text');
                    markRead(selectedFile);
                    setLoading(false);
                    return;
                }

                const documentUrl = selectedFile.document;
                if (!documentUrl) {
                    setError('This file is not available to preview.');
                    setLoading(false);
                    return;
                }

                if (isAudioFile(documentUrl)) {
                    const objectUrl = await createVdrDocumentObjectUrl(selectedFile, {
                        workstream,
                        isInitialProjectFile,
                        forPreview: true,
                    });
                    if (cancelled) return;
                    blobUrlRef.current = objectUrl;
                    setFileUrl(objectUrl);
                    setViewKind('audio');
                    markRead(selectedFile);
                    setLoading(false);
                    return;
                }

                if (!isPDFTronSupportedFile(selectedFile)) {
                    setError('This file type cannot be previewed.');
                    setLoading(false);
                    return;
                }

                const fileExtension = getFileExtensionFromFile(selectedFile);
                let webViewerUrl = documentUrl;
                if (documentUrl.includes('.blob.core.windows.net')) {
                    webViewerUrl = await dispatch(GetAuthenticatedDocUrl(documentUrl)).unwrap();
                }

                if (cancelled) return;
                setFileUrl(webViewerUrl);
                setViewKind('webviewer');

                const protection = await fetchDocumentProtectionCheck(webViewerUrl, fileExtension);
                if (cancelled) return;
                if (protection.protected) {
                    setError(getProtectedDocumentMessage(protection.reason));
                    setViewKind('error');
                    setLoading(false);
                    return;
                }

                if (!viewerRef.current) {
                    setLoading(false);
                    return;
                }

                const instance = await WebViewer.Iframe(
                    getWebViewerIframeOptions({
                        fileUrl: webViewerUrl,
                        extension: fileExtension,
                        filename: selectedFile.document_name,
                    }),
                    viewerRef.current,
                );
                if (cancelled) return;
                instanceRef.current = instance;
                setupWebViewerPreviewInstance(instance, {
                    extension: fileExtension,
                    fileUrl: webViewerUrl,
                    filename: selectedFile.document_name,
                    isDisposed: () => cancelled,
                    onReady: () => {
                        if (!cancelled) setLoading(false);
                    },
                    onDocumentLoadError: loadError => {
                        const kind = classifyDocumentLoadError(loadError);
                        if (kind === 'protected') {
                            setError(getProtectedDocumentMessage('generic_protected'));
                            setViewKind('error');
                        } else if (kind === 'excel_too_large') {
                            setError(EXCEL_TOO_LARGE_PREVIEW_MESSAGE);
                            setViewKind('error');
                        } else {
                            setError('This file could not be previewed.');
                            setViewKind('error');
                        }
                        setLoading(false);
                    },
                    onDocumentLoaded: () => markRead(selectedFile),
                });
            } catch (loadError) {
                if (cancelled) return;
                console.error('VDR preview load failed:', loadError);
                setError('This file could not be previewed.');
                setViewKind('error');
                setLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
            instanceRef.current = null;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [openModal, selectedFile, workstream, dispatch, selectedProject?.id, isInitialProjectFile, markRead, resetViewer]);

    const navigate = direction => {
        if (!folderFiles?.length || !setSelectedFileIndex) return;
        const nextIndex = direction === 'prev' ? selectedFileIndex - 1 : selectedFileIndex + 1;
        if (nextIndex < 0 || nextIndex >= folderFiles.length) return;
        setSelectedFileIndex(nextIndex);
        setSelectedFile(folderFiles[nextIndex]);
    };

    const drawer =
        openModal && selectedFile ? (
            <OverlayDrawer
                size="full"
                position="bottom"
                open={openModal}
                onOpenChange={(_, state) => {
                    if (!state.open) handleClose();
                }}>
                <div className={vdrStyles.fileViewerShell}>
                    <div className={`${viewerStyles.FVHeader} ${vdrStyles.fileViewerHeader}`}>
                        <div className="d-flex align-items-center">
                            <p className={viewerStyles.FVfilePath}>{selectedFile.document_name}</p>
                            {(isProjectAdmin || selectedFile?.can_download_original) && (
                                <button
                                    type="button"
                                    className="btn btn-link text-white ms-3 p-0"
                                    onClick={handleDownload}
                                    aria-label="Download file">
                                    <ArrowDownload16Regular />
                                </button>
                            )}
                        </div>
                        <div className="d-flex align-items-center">
                            {folderFiles.length > 1 && (
                                <div>
                                    <button
                                        type="button"
                                        disabled={selectedFileIndex <= 0}
                                        onClick={() => navigate('prev')}
                                        className={viewerStyles.FVdirectionalBtn}>
                                        Previous
                                    </button>
                                    <span className={viewerStyles.FVfileCount}>
                                        {selectedFileIndex + 1} / {folderFiles.length}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={selectedFileIndex >= folderFiles.length - 1}
                                        onClick={() => navigate('next')}
                                        className={viewerStyles.FVdirectionalBtn}>
                                        Next
                                    </button>
                                </div>
                            )}
                            <Dismiss20Regular role="button" color="white" className="ms-3" onClick={handleClose} />
                        </div>
                    </div>
                    <div className={vdrStyles.fileViewerDocArea}>
                        {loading && viewKind !== 'webviewer' && (
                            <div className={vdrStyles.fileViewerLoader}>
                                <AiOutlineLoading3Quarters size={50} className="rotateLoader" />
                            </div>
                        )}
                        {viewKind === 'text' && !loading ? (
                            <div className={`${viewerStyles.nativeTextPreview} ${vdrStyles.fileViewerNativeText}`}>
                                {nativeTextMode === 'html' ? (
                                    <iframe
                                        title={selectedFile.document_name}
                                        sandbox=""
                                        srcDoc={nativeText}
                                        className={viewerStyles.nativeTextPreviewFrame}
                                    />
                                ) : (
                                    <pre className={viewerStyles.nativeTextPreviewPlain}>{nativeText}</pre>
                                )}
                            </div>
                        ) : null}
                        {viewKind === 'video' && fileUrl ? (
                            <div className={vdrStyles.fileViewerMedia}>
                                {fileUrl.includes('youtube') || fileUrl.includes('youtu.be') ? (
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`https://www.youtube.com/embed/${getYouTubeId(fileUrl)}`}
                                        title={selectedFile.document_name}
                                        frameBorder="0"
                                        allowFullScreen
                                    />
                                ) : (
                                    <video src={fileUrl} controls style={{ height: '100%', width: '100%' }} />
                                )}
                            </div>
                        ) : null}
                        {viewKind === 'audio' && fileUrl ? (
                            <div className={`d-flex align-items-center justify-content-center ${vdrStyles.fileViewerMedia}`}>
                                <audio controls src={fileUrl} style={{ width: 'min(480px, 90%)' }} />
                            </div>
                        ) : null}
                        {viewKind === 'link' ? (
                            <div
                                className={`d-flex flex-column align-items-center justify-content-center ${vdrStyles.fileViewerMedia}`}
                                style={{ gap: 16 }}>
                                <p style={{ margin: 0 }}>{selectedFile.document_name}</p>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => safeOpenUrl(selectedFile.document_link, '_blank')}>
                                    Open link in new tab
                                </button>
                            </div>
                        ) : null}
                        {(viewKind === 'error' || error) && !loading ? (
                            <div
                                className={`d-flex flex-column align-items-center justify-content-center ${vdrStyles.fileViewerMedia}`}
                                style={{ gap: 8 }}>
                                <p style={{ margin: 0, maxWidth: 520, textAlign: 'center' }}>
                                    {error || 'This file type cannot be previewed.'}
                                </p>
                                <p style={{ margin: 0, color: '#666' }}>{selectedFile.document_name}</p>
                            </div>
                        ) : null}
                        <div
                            key={selectedFile?.id || fileUrl || 'viewer'}
                            ref={viewerRef}
                            className={`webviewer ${vdrStyles.fileViewerViewport}`}
                            style={{
                                display: viewKind === 'webviewer' && !error ? 'flex' : 'none',
                                flexDirection: 'column',
                            }}>
                            {loading && viewKind === 'webviewer' && (
                                <div className={vdrStyles.fileViewerLoader}>
                                    <AiOutlineLoading3Quarters size={50} className="rotateLoader" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </OverlayDrawer>
        ) : null;

    if (!drawer) return null;

    return createPortal(drawer, document.body);
};

export default VdrFileViewerModal;
